import { useState, useEffect } from "react";
import "@/App.css";
import "@/utils/authBootstrap"; // side-effect: shims window.fetch with credentials:'include' so the mementa_token cookie is sent on every request
import "@/utils/errorReporter"; // side-effect: installs global window error/unhandledrejection crash reporting
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { logger } from '@/utils/logger';

// Contexts
import { ThemeProvider } from "@/contexts/ThemeContext";

// Shared layout components
import Navigation from "@/components/Navigation";
import ErrorBoundary from "@/components/ErrorBoundary";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import ScrollToTop from "@/components/ScrollToTop";
import { initGA, trackPageView } from "@/utils/ga";

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    initGA();
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}
import TermsAcceptanceModal from "@/components/TermsAcceptanceModal";

// Public + member pages
import Browse from "@/pages/Browse";
import Home from "@/pages/Home";
import Keepsakes from "@/pages/Keepsakes";
import KeepsakeCreate from "@/pages/KeepsakeCreate";
import KeepsakeDetail from "@/pages/KeepsakeDetail";
import KeepsakeEditQuestions from "@/pages/KeepsakeEditQuestions";
import KeepsakeResults from "@/pages/KeepsakeResults";
import RecipientDetail from "@/pages/RecipientDetail";
import ViewResponses from "@/pages/ViewResponses";
import GiftQuestionnaire from "@/pages/GiftQuestionnaire";
import QuestionnaireResponse from "@/pages/QuestionnaireResponse";
import SharedWishlist from "@/pages/SharedWishlist";
import SharedRecipient from "@/pages/SharedRecipient";
import SharedShortlist from "@/pages/SharedShortlist";
import SharedCollection from "@/pages/SharedCollection";
import Brands from "@/pages/Brands";
import BrandProfile from "@/pages/BrandProfile";
import BrandApply from "@/pages/BrandApply";
import BrandDirectoryGate from "@/components/brands/BrandDirectoryGate";
import CollaborativeWishlist from "@/pages/CollaborativeWishlist";
import Subscription from "@/pages/Subscription";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import AffiliateDisclosure from "@/pages/AffiliateDisclosure";
import CookieConsent from "@/components/CookieConsent";
import Unsubscribe from "@/pages/Unsubscribe";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";

// Admin pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminFeaturedSections from "@/pages/admin/AdminFeaturedSections";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminArchetypes from "@/pages/admin/AdminArchetypes";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminAds from "@/pages/admin/AdminAds";
import AdminTerms from "@/pages/admin/AdminTerms";
import AdminSettings from "@/pages/admin/AdminSettings";
import MaintenancePage from "@/pages/MaintenancePage";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminEmailCampaigns from "@/pages/admin/AdminEmailCampaigns";
import AdminClientErrors from "@/pages/admin/AdminClientErrors";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Export axios instance for components
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send the httpOnly `mementa_token` cookie
});

// Export string URL for fetch-based components (admin pages)
export { API_URL as API_STRING };

// No explicit Authorization header, auth flows through the httpOnly cookie
// (backend accepts either cookie or Bearer; this removes the localStorage XSS
// vector). The window.fetch shim in authBootstrap.js sends the cookie via
// `credentials: 'include'` on every request.

// Top-level guard for admin routes. Lives outside `App` so React doesn't see
// a brand-new component type on every parent render, that would force the
// admin subtree to unmount/remount each time and blow away its state.
function AdminRoute({ user, children }) {
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  if (!user.is_admin) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [maintenance, setMaintenance] = useState(null);

  useEffect(() => {
    // Hydrate user state from the backend using the httpOnly cookie.
    // A cached `user` object in localStorage is used for instant UI render
    // (non-sensitive data), then corrected by the `/auth/me` response.
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (_) { /* ignore parse error */ }
    }
    api.get('/auth/me')
      .then((resp) => {
        setUser(resp.data);
        localStorage.setItem('user', JSON.stringify(resp.data));
        checkTermsAcceptance();
      })
      .catch(() => {
        // Not authenticated, clear any stale cache.
        setUser(null);
        localStorage.removeItem('user');
      })
      .finally(() => setLoading(false));
    api.get('/maintenance')
      .then((resp) => setMaintenance(resp.data))
      .catch(() => { /* treat as no maintenance */ });
  }, []);

  const checkTermsAcceptance = async () => {
    try {
      const response = await api.get('/terms/acceptance-required');
      if (response.data.acceptance_required) {
        setShowTermsModal(true);
      }
    } catch (error) {
      logger.error('Error checking terms acceptance:', error);
    }
  };

  const handleTermsAccept = () => {
    setShowTermsModal(false);
  };

  const handleLogin = (userData /*, token (unused, cookie is set by backend) */) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    checkTermsAcceptance();
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (_) { /* ignore; cookie expires anyway */ }
    setUser(null);
    localStorage.removeItem('user');
    // Clean up any legacy token that might still be in localStorage
    localStorage.removeItem('token');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #FED7A5 0%, #73766A 100%)'}}>
        <div className="text-2xl" style={{fontFamily: 'Playfair Display, serif', color: '#20212B'}}>Loading...</div>
      </div>
    );
  }

  const isAdmin = !!user?.is_admin;
  // Whole-site maintenance: everyone except admins sees the "back soon" page.
  // Nav stays visible so an admin can still sign in and lift maintenance.
  if (maintenance?.site_enabled && !isAdmin && !window.location.pathname.startsWith('/admin')) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <BrowserRouter>
            <Navigation user={user} setUser={setUser} />
            <MaintenancePage message={maintenance.message} />
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
  // Per-page maintenance gate (keeps the nav bar).
  const gated = (pageKey, element) =>
    maintenance?.pages?.[pageKey] && !isAdmin
      ? (
        <>
          <Navigation user={user} setUser={setUser} />
          <MaintenancePage message={maintenance.message} pageOnly />
        </>
      )
      : element;

  return (
    <ErrorBoundary>
    <ThemeProvider>
      <div className="App">
        {showTermsModal && (
          <TermsAcceptanceModal
            onAccept={handleTermsAccept}
          />
        )}
        <BrowserRouter>
          <ScrollToTop />
          <RouteTracker />
          <AnnouncementBanner />
          <Routes>
          {/* Public routes */}
          <Route path="/" element={gated('browse',
            <>
              <Navigation user={user} setUser={setUser} />
              <Browse user={user} />
            </>
          )} />
          <Route path="/browse" element={gated('browse',
            <>
              <Navigation user={user} setUser={setUser} />
              <Browse user={user} />
            </>
          )} />
          <Route path="/about" element={gated('about',
            <>
              <Navigation user={user} setUser={setUser} />
              <Home user={user} setUser={setUser} />
            </>
          )} />
          <Route path="/gift-form/:shareToken" element={<GiftQuestionnaire />} />
          
          {/* Keepsakes routes - Recipient Profiles */}
          <Route path="/keepsakes" element={gated('keepsakes',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <Keepsakes user={user} />
              </>
            ) : <Navigate to="/" replace />
          )} />
          <Route path="/keepsakes/:id" element={gated('keepsakes',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <RecipientDetail user={user} />
              </>
            ) : <Navigate to="/" replace />
          )} />
          
          {/* AI Questionnaire routes */}
          <Route path="/keepsakes/create" element={gated('keepsakes',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <KeepsakeCreate user={user} />
              </>
            ) : <Navigate to="/" replace />
          )} />
          <Route path="/keepsakes/:id/questionnaire" element={gated('keepsakes',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <KeepsakeDetail user={user} />
              </>
            ) : <Navigate to="/" replace />
          )} />
          <Route path="/keepsakes/:id/edit" element={gated('keepsakes',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <KeepsakeEditQuestions />
              </>
            ) : <Navigate to="/" replace />
          )} />
          <Route path="/keepsakes/:id/results" element={gated('keepsakes',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <KeepsakeResults user={user} />
              </>
            ) : <Navigate to="/" replace />
          )} />
          <Route path="/keepsakes/:id/responses" element={gated('keepsakes',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <ViewResponses user={user} />
              </>
            ) : <Navigate to="/" replace />
          )} />
          
          {/* Public questionnaire response page */}
          <Route path="/gift-questionnaire/:shareId" element={<QuestionnaireResponse />} />
          
          {/* Public shared wishlist page */}
          <Route path="/w/:shareId" element={<SharedWishlist />} />
          <Route path="/shared/recipient/:shareToken" element={<SharedRecipient />} />
          <Route path="/shared/:token" element={<SharedShortlist />} />
          <Route path="/collection/:sectionId" element={<SharedCollection user={user} />} />
          <Route path="/brands" element={<BrandDirectoryGate user={user}><Brands user={user} /></BrandDirectoryGate>} />
          <Route path="/brands/apply" element={<BrandDirectoryGate user={user}><BrandApply /></BrandDirectoryGate>} />
          <Route path="/brands/:slug" element={<BrandDirectoryGate user={user}><BrandProfile user={user} /></BrandDirectoryGate>} />
          <Route path="/wishlists/collaborative/:shareToken" element={<CollaborativeWishlist />} />
          <Route path="/pricing" element={<Subscription />} />
          
          {/* Terms of Service */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
          
          {/* Unsubscribe - Public page */}
          <Route path="/unsubscribe/:userId" element={<Unsubscribe />} />
          
          {/* Email Verification - Public page */}
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Wishlist - Redirect to Keepsakes tab */}
          <Route path="/wishlist" element={<Navigate to="/keepsakes?tab=wishlist" replace />} />
          
          {/* Profile */}
          <Route path="/profile" element={gated('profile',
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <Profile user={user} setUser={setUser} />
              </>
            ) : <Navigate to="/" replace />
          )} />
          
          <Route path="/notifications" element={
            user ? (
              <>
                <Navigation user={user} setUser={setUser} />
                <Notifications user={user} />
              </>
            ) : <Navigate to="/" replace />
          } />
          
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin onLogin={handleLogin} />} />
          <Route path="/admin" element={
            <AdminRoute user={user}>
              <AdminLayout user={user} onLogout={handleLogout} />
            </AdminRoute>
          }>
            <Route path="dashboard" element={<Navigate to="/admin/analytics" replace />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="featured-sections" element={<AdminFeaturedSections />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="archetypes" element={<AdminArchetypes />} />
            <Route path="brands" element={<AdminBrands />} />
            <Route path="ads" element={<AdminAds />} />
            {/* Legacy bookmarks, these areas now live inside the Analytics
                control center. */}
            <Route path="products" element={<Navigate to="/admin/analytics?manage=products" replace />} />
            <Route path="users" element={<Navigate to="/admin/analytics?manage=users" replace />} />
            <Route path="merchants" element={<Navigate to="/admin/analytics?manage=merchants" replace />} />
            <Route path="terms" element={<AdminTerms />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="email-campaigns" element={<AdminEmailCampaigns />} />
            <Route path="client-errors" element={<AdminClientErrors />} />
            <Route index element={<Navigate to="/admin/analytics" replace />} />
          </Route>
          </Routes>
        </BrowserRouter>
        <CookieConsent />
        <Toaster position="top-right" />
      </div>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;